/**
 * ClinicsOverviewHeader — overview tiles (NivOverviewStats) + two equal
 * insight panels: incoming payments per month (12 months, pre-VAT, from the
 * manual payment logs) and the 12-week signup sparkline. Display-only.
 */

import { useMemo } from "react";
import { Info } from "@phosphor-icons/react";
import HoverTip from "../../../../components/ui/HoverTip";
import useSparklinePath from "../shared/hooks/useSparklinePath";
import useCountUp from "../shared/hooks/useCountUp";
import NivOverviewStats from "../shared/NivOverviewStats";
import { buildMonthlyPayments, buildWeeklySignups, formatMonthKey, formatShekels } from "../shared/nivDerived";

const PAYMENT_MONTHS = 12;

/** "YYYY-MM" → short Hebrew month initial + 2-digit year for the axis */
const shortMonth = (monthKey) => {
  const full = formatMonthKey(monthKey); // "אוקטובר 2026"
  const [name, year] = full.split(" ");
  return `${(name || "").slice(0, 3)} ${(year || "").slice(2)}`;
};

function PaymentsByMonth({ rows }) {
  const buckets = useMemo(() => buildMonthlyPayments(rows, PAYMENT_MONTHS), [rows]);
  const max = Math.max(1, ...buckets.map((b) => b.total));
  const current = buckets[buckets.length - 1];
  const previous = buckets[buckets.length - 2];
  const currentShown = useCountUp(Math.round(current?.total || 0));
  const yearTotal = buckets.reduce((sum, b) => sum + b.total, 0);
  const hasData = buckets.some((b) => b.count > 0);
  const delta = previous?.total ? Math.round(((current.total - previous.total) / previous.total) * 100) : null;

  return (
    <div className="nivck2-panel nivck2-payments">
      <div className="nivck2-panel-title">
        <span>תשלומים נכנסים — לפי חודש</span>
        <HoverTip text="סכום התשלומים שנרשמו ביומן התשלומים של כל קליניקה, לפי חודש התשלום. לפני מע״מ.">
          <Info size={12} className="nivck2-info" tabIndex={0} />
        </HoverTip>
        <span className="nivck2-panel-kpi">
          <strong>{formatShekels(currentShown)}</strong>
          <span>החודש</span>
          {delta !== null && (
            <span className={`nivck2-delta${delta >= 0 ? " is-up" : " is-down"}`}>
              {delta >= 0 ? "+" : ""}
              {delta}%
            </span>
          )}
        </span>
      </div>

      {!hasData ? (
        <div className="nivck2-panel-empty">אין תשלומים רשומים ב-12 החודשים האחרונים</div>
      ) : (
        <div className="nivck2-bars" role="img" aria-label="תשלומים נכנסים לפי חודש">
          {buckets.map((b, i) => (
            <div
              key={b.month}
              className={`nivck2-bar-col${i === buckets.length - 1 ? " is-current" : ""}`}
              style={{ "--nivck2-i": i }}
              title={`${formatMonthKey(b.month)}: ${formatShekels(b.total)} · ${b.count} תשלומים`}
            >
              <span className="nivck2-bar-value">{b.total > 0 ? formatShekels(Math.round(b.total)) : ""}</span>
              <span className="nivck2-bar-track">
                <span className="nivck2-bar-fill" style={{ "--nivck2-fill": `${Math.max(2, Math.round((b.total / max) * 100))}%` }} />
              </span>
              <span className="nivck2-bar-label">{shortMonth(b.month)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="nivck2-panel-foot">
        <span>12 חודשים: {formatShekels(Math.round(yearTotal))}</span>
        <span>ממוצע חודשי: {formatShekels(Math.round(yearTotal / PAYMENT_MONTHS))}</span>
      </div>
    </div>
  );
}

function WeeklySignups({ rows }) {
  const weekly = useMemo(() => buildWeeklySignups(rows, 12), [rows]);
  const values = useMemo(() => weekly.map((w) => w.count), [weekly]);
  const spark = useSparklinePath(values, { w: 220, h: 56, pad: 5 });
  const last4 = values.slice(-4).reduce((a, b) => a + b, 0);
  const prev4 = values.slice(-8, -4).reduce((a, b) => a + b, 0);
  const total12 = values.reduce((a, b) => a + b, 0);

  return (
    <div className="nivck2-panel nivck2-spark">
      <div className="nivck2-panel-title">
        <span>הרשמות — 12 שבועות</span>
        <span className="nivck2-panel-kpi">
          <strong>{last4}</strong>
          <span>ב-4 שבועות אחרונים</span>
          <span className={`nivck2-delta${last4 >= prev4 ? " is-up" : " is-down"}`}>
            {last4 >= prev4 ? "+" : ""}
            {last4 - prev4}
          </span>
        </span>
      </div>
      <svg
        className="nivck2-spark-svg"
        viewBox={`0 0 ${spark.w} ${spark.h}`}
        preserveAspectRatio="none"
        aria-label="הרשמות שבועיות"
        role="img"
      >
        {spark.points && (
          <>
            <polyline className="nivck2-spark-line" points={spark.points} pathLength="1" />
            {spark.last && <circle className="nivck2-spark-dot" cx={spark.last.x} cy={spark.last.y} r="3" />}
          </>
        )}
      </svg>
      <div className="nivck2-spark-axis">
        <span>לפני 12 שב׳</span>
        <span>השבוע</span>
      </div>
      <div className="nivck2-panel-foot">
        <span>12 שבועות: {total12} הרשמות</span>
        <span>4 שבועות קודמים: {prev4}</span>
      </div>
    </div>
  );
}

function ClinicsOverviewHeader({ rows, inactiveCount = 0 }) {
  return (
    <div className="nivck2-header">
      <NivOverviewStats rows={rows} inactiveCount={inactiveCount} />
      <div className="nivck2-insights">
        <PaymentsByMonth rows={rows} />
        <WeeklySignups rows={rows} />
      </div>
    </div>
  );
}

export default ClinicsOverviewHeader;
